import { useState } from "react";
import { Lock, Pencil, Check, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useBlockLease } from "@/hooks/useBlockLease";
import { useMe } from "@/lib/rehearsalRoomQueries";
import { useAdminReleaseLease } from "@/lib/workDocumentQueries";
import AutosaveStatus from "../presentation/AutosaveStatus";
import { SemanticContentView } from "./blocks/SemanticContentView";
import { RichInputEditor } from "./blocks/RichInputEditor";
import { ListInputEditor } from "./blocks/ListInputEditor";
import { ChecklistInputEditor } from "./blocks/ChecklistInputEditor";
import { TableInputEditor } from "./blocks/TableInputEditor";
import { TypedValuePreview } from "./blocks/TypedValuePreview";

const STRUCTURED_VALUE_KEY = { list: "list_items", checklist: "checklist_items", table: "table" };
const STRUCTURED_EDITOR = { list: ListInputEditor, checklist: ChecklistInputEditor, table: TableInputEditor };

/**
 * Master v5 Prompt 8A — one block, one lease lifecycle. Extended in
 * Prompt 30C Phase 2 with explicit `block_type`/`content.input_type`
 * branching (previously every block rendered as the same plain
 * textarea regardless of type).
 *
 * Locked-concurrency contract (unchanged from Prompt 8A, applies
 * uniformly across every editable type below):
 *  - `state === "editing_by_other"` -> permanently read-only for this
 *    viewer with the exact required copy, never an edit affordance.
 *  - starting to edit acquires a lease; only success unlocks the editor.
 *  - remote content updates (via the parent document refetch) never
 *    replace the live editor value while `dirty` is true — see
 *    useBlockLease's effect guard.
 *  - a 409 on save never overwrites local text; it surfaces a compare/
 *    keep/discard choice instead.
 */
export default function BlockCard({ workspaceId, docId, block, isViewAsClient, isDocumentSubmitted }) {
  const isStaticContent = block.block_type === "content";
  const inputType = block.content?.input_type || "long"; // legacy manual blocks have no input_type -> the original plain-textarea behavior
  const valueKey = STRUCTURED_VALUE_KEY[inputType] || "text";
  const lease = useBlockLease({ workspaceId, docId, serverBlock: block, isViewAsClient, valueKey });
  const [compareOpen, setCompareOpen] = useState(false);
  const { data: me } = useMe();
  const isCoach = me?.portal_role === "coach_admin";
  const adminRelease = useAdminReleaseLease(workspaceId, docId);

  const documentReadOnly = isDocumentSubmitted || isViewAsClient;
  const policyDenied = !documentReadOnly && !block.editable && block.state !== "editing_by_other" && !lease.isMine;
  const readOnly = documentReadOnly || policyDenied;

  if (isStaticContent) {
    return (
      <div className="border rounded-lg p-4 border-ink/15 bg-white/50" data-testid={`work-document-block-${block.block_id}`}>
        <SemanticContentView semanticContent={block.content?.semantic_content} testId={`work-document-block-${block.block_id}-static-content`} />
      </div>
    );
  }

  const StructuredEditor = STRUCTURED_EDITOR[inputType];

  return (
    <div
      className={`border rounded-lg p-4 ${lease.isMine ? "border-terracotta bg-terracotta/5" : "border-ink/15 bg-white/50"}`}
      data-testid={`work-document-block-${block.block_id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div>
          <p className="text-sm font-medium text-ink" data-testid={`work-document-block-${block.block_id}-label`}>
            {block.content?.label || block.block_key.replace(/-/g, " ")}
          </p>
          {block.content?.help_text && (
            <p className="text-xs text-ink-soft mt-0.5" data-testid={`work-document-block-${block.block_id}-help`}>{block.content.help_text}</p>
          )}
        </div>
        {lease.isMine && lease.saveState !== "idle" && <AutosaveStatus status={lease.saveState === "locked" ? "error" : lease.saveState} testId={`work-document-block-${block.block_id}-autosave-status`} />}
      </div>

      {block.state === "editing_by_other" && (
        <div className="mb-2">
          <p className="text-sm text-mustard-dark italic" data-testid={`work-document-block-${block.block_id}-locked-message`}>
            Another user is editing this section right now
          </p>
          {isCoach && (
            <button
              onClick={async () => {
                const reason = window.prompt("Reason for forcing this lock open (recorded in the audit log):");
                if (!reason) return;
                try {
                  await adminRelease.mutateAsync({ blockId: block.block_id, reason });
                  toast.success("Lock released — this section is now open to edit");
                } catch (err) {
                  toast.error(err.body?.detail || "Couldn't release this lock");
                }
              }}
              disabled={adminRelease.isPending}
              className="mt-1 inline-flex items-center gap-1 text-xs text-terracotta hover:text-terracotta/80"
              data-testid={`work-document-block-${block.block_id}-admin-release`}
            >
              <ShieldAlert size={11} /> Force-release this lock (admin)
            </button>
          )}
        </div>
      )}

      {lease.recoverableDraft && !lease.isMine && (
        <div className="border border-mustard/50 bg-mustard/10 rounded-md p-3 mb-3 text-xs" data-testid={`work-document-block-${block.block_id}-recovery-banner`}>
          <p className="text-ink mb-2">You have unsaved changes from an earlier session that were never confirmed as saved.</p>
          <div className="flex items-center gap-3">
            <button onMouseDown={(e) => e.preventDefault()} onClick={lease.keepLocalDraft} className="font-medium text-terracotta hover:text-terracotta/80" data-testid={`work-document-block-${block.block_id}-recovery-keep`}>
              Restore my draft
            </button>
            <button onMouseDown={(e) => e.preventDefault()} onClick={lease.discardLocalDraft} className="text-ink-soft hover:text-ink" data-testid={`work-document-block-${block.block_id}-recovery-discard`}>
              Discard, use latest
            </button>
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => setCompareOpen((v) => !v)} className="text-ink-soft hover:text-ink underline" data-testid={`work-document-block-${block.block_id}-recovery-compare-toggle`}>
              Compare
            </button>
          </div>
          {compareOpen && (
            <div className="mt-2 grid gap-2" data-testid={`work-document-block-${block.block_id}-recovery-compare-view`}>
              <div>
                <p className="text-ink-soft mb-0.5">Your unsaved draft:</p>
                <p className="text-ink bg-white/70 rounded px-2 py-1 whitespace-pre-wrap" data-testid={`work-document-block-${block.block_id}-recovery-compare-local`}>{typeof lease.recoverableDraft.value === "string" ? lease.recoverableDraft.value : JSON.stringify(lease.recoverableDraft.value)}</p>
              </div>
              <div>
                <p className="text-ink-soft mb-0.5">Latest saved version:</p>
                <p className="text-ink bg-white/70 rounded px-2 py-1 whitespace-pre-wrap" data-testid={`work-document-block-${block.block_id}-recovery-compare-server`}>{typeof block.content?.[valueKey] === "string" ? block.content[valueKey] : JSON.stringify(block.content?.[valueKey] || "")}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {lease.saveState === "conflict" && (
        <div className="border border-mustard/50 bg-mustard/10 rounded-md p-3 mb-3 text-xs" data-testid={`work-document-block-${block.block_id}-conflict-banner`}>
          <p className="text-ink mb-2">Someone else saved a newer version of this section while you were editing. Your changes were not lost — choose what to do:</p>
          <div className="flex items-center gap-3">
            <button onMouseDown={(e) => e.preventDefault()} onClick={lease.acceptLatestOnConflict} className="font-medium text-terracotta hover:text-terracotta/80" data-testid={`work-document-block-${block.block_id}-conflict-view-latest`}>
              View latest (discard my edit)
            </button>
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => setCompareOpen((v) => !v)} className="text-ink-soft hover:text-ink underline" data-testid={`work-document-block-${block.block_id}-conflict-compare-toggle`}>
              Compare
            </button>
            <span className="text-ink-soft">Nothing was silently overwritten.</span>
          </div>
          {compareOpen && (
            <div className="mt-2 grid gap-2" data-testid={`work-document-block-${block.block_id}-conflict-compare-view`}>
              <div>
                <p className="text-ink-soft mb-0.5">Your unsaved version (still active below):</p>
                <p className="text-ink bg-white/70 rounded px-2 py-1 whitespace-pre-wrap" data-testid={`work-document-block-${block.block_id}-conflict-compare-local`}>{typeof lease.localValue === "string" ? lease.localValue : JSON.stringify(lease.localValue)}</p>
              </div>
              <div>
                <p className="text-ink-soft mb-0.5">Latest saved version (from the other save):</p>
                <p className="text-ink bg-white/70 rounded px-2 py-1 whitespace-pre-wrap" data-testid={`work-document-block-${block.block_id}-conflict-compare-server`}>{typeof lease.conflictDoc?.content?.[valueKey] === "string" ? lease.conflictDoc.content[valueKey] : JSON.stringify(lease.conflictDoc?.content?.[valueKey] || "")}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {lease.isMine ? (
        StructuredEditor ? (
          <StructuredEditor value={lease.localValue} onChange={lease.onChangeValue} testId={`work-document-block-${block.block_id}-editor`} />
        ) : inputType === "rich" ? (
          <RichInputEditor value={lease.localValue} onChange={lease.onChangeValue} testId={`work-document-block-${block.block_id}-editor`} />
        ) : inputType === "short" ? (
          <Input
            value={lease.localValue || ""}
            onChange={(e) => lease.onChangeValue(e.target.value)}
            onBlur={() => lease.stopEditing()}
            autoFocus
            className="bg-white"
            data-testid={`work-document-block-${block.block_id}-editor`}
          />
        ) : (
          <Textarea
            value={lease.localValue || ""}
            onChange={(e) => lease.onChangeValue(e.target.value)}
            onBlur={() => lease.stopEditing()}
            autoFocus
            rows={4}
            className="bg-white"
            data-testid={`work-document-block-${block.block_id}-editor`}
          />
        )
      ) : (
        <div
          onClick={() => !readOnly && block.state !== "editing_by_other" && lease.startEditing()}
          className={`text-sm text-ink min-h-[3rem] rounded-md px-2 py-1.5 -mx-2 ${
            !readOnly && block.state !== "editing_by_other" ? "cursor-text hover:bg-ink/5" : ""
          }`}
          data-testid={`work-document-block-${block.block_id}-preview`}
        >
          <TypedValuePreview inputType={inputType} value={lease.localValue} />
        </div>
      )}

      <div className="flex items-center gap-2 mt-2">
        {readOnly && block.state !== "editing_by_other" && (
          <span className="inline-flex items-center gap-1 text-xs text-ink-soft" data-testid={`work-document-block-${block.block_id}-readonly-badge`}>
            <Lock size={11} /> Read-only
          </span>
        )}
        {!readOnly && !lease.isMine && block.state !== "editing_by_other" && (
          <button onClick={lease.startEditing} className="inline-flex items-center gap-1 text-xs text-terracotta hover:text-terracotta/80" data-testid={`work-document-block-${block.block_id}-start-edit`}>
            <Pencil size={11} /> Click to edit
          </button>
        )}
        {lease.isMine && (
          <button onClick={lease.stopEditing} className="inline-flex items-center gap-1 text-xs text-ink-soft hover:text-ink" data-testid={`work-document-block-${block.block_id}-done-edit`}>
            <Check size={11} /> Done editing
          </button>
        )}
      </div>
      {lease.saveState === "locked" && (
        <p className="text-xs text-mustard-dark mt-1" data-testid={`work-document-block-${block.block_id}-locked-notice`}>
          Another user is editing this section right now
        </p>
      )}
    </div>
  );
}
